import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Typography, Stack, Button, Divider } from '@mui/material';
import { Page, PageTitle, Sheet, ActionBar } from '../../../components';
import useFetch from '../../../hooks/useFetch';
import { formatCents } from '../money';

const CANCEL_REASON = 'paid before tracking';

export default function NeedsAttention({ auth, setAuth }) {
  const { t } = useTranslation();
  const { fetchResource, showAlert, alertMessage, setShowAlert, setAlertMessage } = useFetch({ setAuth });
  const [d, setD] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const canManage = Boolean(auth.perms?.CAL_MANAGE);
  const load = () => fetchResource({ method: 'GET', resource: 'calendar/attention' }).then(r => !r.error && setD(r));
  useEffect(() => { if (canManage) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, body = {}) => {
    const r = await fetchResource({ resource: path, body });
    if (r.error) { setAlertMessage({ message: r.error, type: 'error' }); setShowAlert(true); }
    load();
  };
  const cancel = id => act(`calendar/outflows/${id}/cancel`, { reason: CANCEL_REASON });
  const confirmCancel = () => { cancel(confirmTarget.id); setConfirmTarget(null); };

  if (!canManage) return <Page><PageTitle>{t('calendar.admin.attention')}</PageTitle></Page>;

  const Row = ({ children, actions }) => <Stack direction='row' spacing={1} alignItems='center'><Typography sx={{ flex: 1 }}>{children}</Typography>{actions}</Stack>;

  return (
    <Page>
      <PageTitle>{t('calendar.admin.attention')}</PageTitle>
      {showAlert && <Alert severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>}
      {d && (
        <>
          <Typography variant='h6'>{t('calendar.admin.unmatched')}</Typography>
          {d.unmatched.checks.map(c => <Row key={`c${c.id ?? c.check_number}`} actions={<Button component={Link} to='/calendar'>{t('calendar.add')}</Button>}>#{c.check_number} {formatCents(c.amount_cents)} {c.date}</Row>)}
          {d.unmatched.debits.map(x => <Row key={`d${x.id}`}>{x.name} {formatCents(x.amount_cents)} {x.date}</Row>)}
          <Divider sx={{ my: 1 }} />
          <Typography variant='h6'>{t('calendar.admin.waiting')}</Typography>
          {d.waiting.probable.map(p => (
            <Row key={`p${p.id}`} actions={<><Button onClick={() => act(`calendar/outflows/${p.id}/confirm-probable`)}>{t('calendar.admin.confirm')}</Button><Button onClick={() => act(`calendar/outflows/${p.id}/reject-probable`)}>{t('calendar.admin.reject')}</Button></>}>
              {p.payee} #{p.check_number} {formatCents(p.amount_cents)}
            </Row>
          ))}
          {[...d.waiting.lateBills, ...d.waiting.overdueChecks].map(o => (
            <Row key={`o${o.id}`} actions={<Button onClick={() => setConfirmTarget(o)}>{t('calendar.admin.cancel')}</Button>}>{o.payee} {formatCents(o.amount_cents)} {o.due_date}</Row>
          ))}
          {d.waiting.hintless.map(b => <Row key={`h${b.id}`} actions={<Button component={Link} to='/calendar-bills'>{t('calendar.admin.hint')}</Button>}>{b.payee}</Row>)}
          {d.waiting.bounced.map((b, i) => <Row key={`b${i}`}>{b.payee} #{b.check_number} {formatCents(b.amount_cents)} — {b.reason}</Row>)}
          {d.waiting.alerts.map((a, i) => <Row key={`a${i}`}>{a.message ?? JSON.stringify(a)}</Row>)}
          <Divider sx={{ my: 1 }} />
          <Typography variant='h6'>{t('calendar.admin.invoicesOff')}</Typography>
          {d.invoices.map(i => (
            <Row key={`i${i.id}`} actions={i.vendor_id ? <Button component={Link} to={`/calendar-vendor/${i.vendor_id}`}>{i.vendor}</Button> : undefined}>
              {i.vendor_id ? '' : `${i.vendor} `}{i.kind === 'opening_balance' ? t('calendar.vendorChecks.openingBalance') : i.invoice_number} — {formatCents(i.uncoveredCents)}
            </Row>
          ))}
          {d.seedReview.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant='h6'>{t('calendar.admin.seedReview')}</Typography>
              {d.seedReview.map(o => <Row key={`s${o.id}`} actions={<Button onClick={() => setConfirmTarget(o)}>{t('calendar.admin.cancel')}</Button>}>{o.payee} #{o.check_number} {formatCents(o.amount_cents)} {o.due_date}</Row>)}
            </>
          )}
          <Button variant='outlined' sx={{ mt: 2 }} onClick={() => act('calendar/transcription-complete')}>{t('calendar.admin.signOff')}</Button>
        </>
      )}
      {confirmTarget && (
        <Sheet open onClose={() => setConfirmTarget(null)} title={t('calendar.admin.cancel')}
          actions={
            <ActionBar
              cancel={{ label: t('calendar.dismiss'), onClick: () => setConfirmTarget(null) }}
              confirm={{ label: t('calendar.admin.cancel'), onClick: confirmCancel, tone: 'danger' }}
            />
          }>
          <Typography>
            {confirmTarget.payee}{confirmTarget.check_number ? ` #${confirmTarget.check_number}` : ''} · {formatCents(confirmTarget.amount_cents)} · {confirmTarget.due_date}
          </Typography>
          <Typography color='text.secondary'>{t('calendar.admin.paidBeforeTracking')}</Typography>
        </Sheet>
      )}
    </Page>
  );
}
